import { useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import type { UnreadBillingAnswer } from "@/lib/timesheets";

export function BillingUnreadAnswersBanner({
  items,
  onOpen,
}: {
  items: UnreadBillingAnswer[];
  onOpen: (timesheetId: number) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (items.length === 0) return null;

  const count = items.length;

  function handleOpenTimesheet(timesheetId: number) {
    setDialogOpen(false);
    onOpen(timesheetId);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        className={clsx(
          "flex w-full flex-wrap items-center justify-between gap-2 rounded-ios-lg border border-status-green/30",
          "bg-status-green/[0.07] px-3 py-2.5 text-left transition hover:bg-status-green/[0.11] sm:px-4 sm:py-3",
        )}
        aria-label="Neue Rückfragen-Antworten anzeigen"
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-label">
            <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-status-green/15 text-xs text-status-green">
              ✓
            </span>
            {count === 1
              ? "1 neue Rückfragen-Antwort"
              : `${count} neue Rückfragen-Antworten`}
          </p>
          <p className="mt-0.5 text-xs text-label-secondary">
            {count === 1
              ? "Eine Antwort liegt vor — zum Anzeigen klicken."
              : `${count} Antworten liegen vor — zum Anzeigen klicken.`}
          </p>
        </div>
        <span className="shrink-0 text-xs font-medium text-accent">Anzeigen</span>
      </button>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={
          count === 1 ? "1 neue Rückfragen-Antwort" : `${count} neue Rückfragen-Antworten`
        }
        description="Antworten in der Abrechnung prüfen und weiterbearbeiten."
        size="lg"
      >
        <ul className="space-y-1.5">
          {items.map((ts) => {
            const number = String(ts.display_number || "").trim() || String(ts.id);
            const project = String(ts.auftrag_display || "").trim();

            return (
              <li
                key={ts.id}
                className="flex flex-col gap-2 rounded-ios border border-separator bg-card-elevated px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold tabular-nums text-label">SN {number}</p>
                  {project ? (
                    <p className="truncate text-xs text-label-secondary">{project}</p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="shrink-0"
                  onClick={() => handleOpenTimesheet(ts.id)}
                >
                  Abrechnung öffnen
                </Button>
              </li>
            );
          })}
        </ul>
      </Dialog>
    </>
  );
}
